/**
 * ProtectedRoute Component
 * 
 * Higher-order component that wraps routes to enforce role-based access control.
 * Redirects unauthorized users to appropriate pages based on their role.
 * Also supports permission-based access control with role-based fallback.
 */

import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getWithAuth } from '../utils/apiClient';
import { ROLE_TEMPLATES } from '../utils/permissionConstants';
import { logger } from "../utils/logger";

/**
 * Check if a user's role grants a specific permission via ROLE_TEMPLATES.
 * Used as a fallback when the permissions API call fails.
 *
 * SECURITY NOTE: This trades precision for availability. If a user's permission
 * is explicitly revoked in the DB, the fallback will still grant access based on
 * their role template during API outages. This matches the existing pattern in
 * usePermissions hook (hooks/usePermissions.ts:42-48) which uses the same
 * ROLE_TEMPLATES fallback when outside a PermissionProvider context.
 */
const checkRoleFallback = (user: { role?: string; roles?: string[] } | null, permissionKey: string): boolean => {
  if (!user) return false;

  // Check primary role
  if (user.role) {
    const rolePerms = ROLE_TEMPLATES[user.role as keyof typeof ROLE_TEMPLATES];
    if (rolePerms && rolePerms[permissionKey as keyof typeof rolePerms]) return true;
  }

  // Check all roles array
  if (user.roles && Array.isArray(user.roles)) {
    for (const role of user.roles) {
      const rolePerms = ROLE_TEMPLATES[role as keyof typeof ROLE_TEMPLATES];
      if (rolePerms && rolePerms[permissionKey as keyof typeof rolePerms]) return true;
    }
  }

  return false;
};

const ProtectedRoute = ({ children, allowedRoles = [], requiresPermission = '' }) => {
  const { user, loading } = useAuth();
  const [permissionLoading, setPermissionLoading] = useState(!!requiresPermission);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      if (!requiresPermission || !user) {
        setPermissionLoading(false);
        return;
      }

      try {
        const effectiveUserId = user.impersonates_user_id || user.uuid;
        const response = await getWithAuth(`/api/permissions/user/${effectiveUserId}`);
        if (response.ok) {
          const data = await response.json();
          const userPermissions = data.permissions || [];
          
          const permission = userPermissions.find(
            p => p.permission_key === requiresPermission && p.is_granted
          );
          
          setHasPermission(!!permission);
        } else {
          // API returned non-OK status — fall back to role-based check
          logger.warn(`Permission API returned ${response.status}, using role-based fallback`);
          setHasPermission(checkRoleFallback(user, requiresPermission));
        }
      } catch (error) {
        // Network error or other failure — fall back to role-based check
        logger.warn('Permission API unreachable, using role-based fallback:', error);
        setHasPermission(checkRoleFallback(user, requiresPermission));
      } finally {
        setPermissionLoading(false);
      }
    };

    checkPermission();
  }, [requiresPermission, user]);

  if (loading || permissionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const userHasRole = (role) => {
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.includes(role);
    }
    return user.role === role;
  };

  const userHasAnyRole = (roles) => {
    if (!roles || roles.length === 0) return true;
    return roles.some(role => userHasRole(role));
  };

  if (allowedRoles && !userHasAnyRole(allowedRoles)) {
    if (userHasRole('client')) {
      return <Navigate to="/client-portal" replace />;
    } else if (userHasRole('provider')) {
      return <Navigate to="/dashboard" replace />;
    } else if (userHasRole('admin')) {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  if (requiresPermission && !hasPermission) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
